import { AxiosError } from 'axios';
import { debounce, isEqual } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useInterval, useLocalStorage } from 'react-use';

import { UserInfo } from '@td-client-frontend/common';

import { PiwikAction, PiwikCategory, PiwikName } from '../@types/common';
import { trackEvent } from '../api/piwik/piwikService';
import { useModal } from '../components/Modal';
import { PermMode } from '../components/WithPermission';
import { useAxios } from './AxiosContext';
import { Permissions } from '../@types/Permissions';

const AuthContext = createContext<AuthContextType>({
  authState: {
    userInfo: {},
    isAuthenticated: false,
    permissions: []
  },
  onPasswordChange: () => {},
  fetchPermissions: () => Promise.resolve(),
  logout: async () => {},
  manualCheck: () => Promise.resolve([]),
  authorizationPermissions: {},
  checkPerm: () => false,
  login: async (username: string, password: string) => false,
  showNoAccessModal: () => {}
});

const { Provider } = AuthContext;

export type WithError<T> = { errors?: string[] } & T;

type AuthorizationPermissions = Record<string, boolean>;

export type AuthState = {
  userInfo: UserInfo | null;
  isAuthenticated: boolean;
  permissions: string[];
};

type AuthContextType = {
  authState: AuthState;
  onPasswordChange: () => void;
  fetchPermissions: () => Promise<void>;
  logout: (arg0?: boolean) => Promise<void>;
  login: (username: string, password: string) => Promise<boolean>;
  manualCheck: () => Promise<string[]>;
  authorizationPermissions: AuthorizationPermissions;
  checkPerm: (requiredPerms: string | string[], mode?: PermMode, assumedPerms?: string[]) => boolean;
  showNoAccessModal: () => void;
};

const forceLogoutModalCfg = {
  header: 'Duplicate Session Detected',
  content: (
    <p className="w-full p-6 font-bold">
      Your account is in use somewhere else, you have been logged out for your security
    </p>
  ),
  actions: [{ title: 'Ok' }]
};

const permissionsChangedModalCfg = {
  header: 'Permissions Changed',
  content: (
    <p className="w-full p-6 font-bold">
      Your permissions have changed, you have been redirected according to your updated permissions
    </p>
  ),
  actions: [{ title: 'Ok' }]
};

interface PermMap {
  [key: string]: string[];
}

function hasPerm(userPerms: any[], perm: string): boolean {
  return process.env.REACT_APP_ROLES_AND_PERMS ? userPerms?.includes(perm) : true;
}
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const delay = 45000;
  const history = useHistory();
  const axiosContext = useAxios();
  const { showModal } = useModal();
  const [authStateLS, setAuthStateLS, removeAuthStateLS] = useLocalStorage<AuthState>('clientLS');
  const [authState, setAuthState] = useState<AuthState>(
    authStateLS || {
      userInfo: null,
      isAuthenticated: false,
      permissions: []
    }
  );

  const checkPerm = useCallback(
    (mode: PermMode, requiredPerms: string | string[], assumedPerms?: string[]): boolean => {
      const userPerms: string[] = assumedPerms || authState.permissions;
      const requiredPermsArray: string[] = Array.isArray(requiredPerms) ? requiredPerms : [requiredPerms];
      const hasAll = requiredPermsArray.every((perm: string) => hasPerm(userPerms, perm));
      const hasAny = requiredPermsArray.some((perm: string) => hasPerm(userPerms, perm));
      return (mode === PermMode.ALL && hasAll) || (mode === PermMode.ANY && hasAny); // should show
    },
    [authState.permissions]
  );

  const checkPerms = useCallback(
    (mode: PermMode, permMap: PermMap) => {
      const res: AuthorizationPermissions = {};
      Object.keys(permMap).map((key) => (res[key] = checkPerm(mode, permMap[key])));
      return res;
    },
    [checkPerm]
  );

  const permManualCheck = useCallback(
    (requiredPerms: string | string[], mode: PermMode = PermMode.ALL, assumedPerms?: string[]) =>
      checkPerm(mode, requiredPerms, assumedPerms),
    [checkPerm]
  );

  const authorizationPermissions: AuthorizationPermissions = useMemo(() => {
    if (!authState?.permissions?.length && !authState.isAuthenticated) {
      return {};
    }
    return checkPerms(PermMode.ANY, {
      groups: [Permissions.GroupRead, Permissions.GroupWrite, Permissions.DraftRead, Permissions.DraftWrite],
      roles: [Permissions.RoleRead, Permissions.RoleWrite, Permissions.RoleCreate],
      users: [Permissions.UserRead, Permissions.UserWrite, Permissions.UserCreate],
      admin: [Permissions.AdminRead, Permissions.AdminWrite],
      reports: [Permissions.ReportEligibilityRead],
      reseller: [Permissions.ResellerRead, Permissions.ResellerWrite, Permissions.ResellerCreate],
      activity: [Permissions.AuditRead, Permissions.AuditWrite, Permissions.AuditCreate],
      members: [Permissions.MemberRead, Permissions.MemberWrite]
    });
  }, [authState, checkPerms]);

  useEffect(() => {
    if (authState.isAuthenticated && authState.userInfo?.shouldUpdatePassword) {
      history.push('/change-password');
    }
  }, [authState.isAuthenticated, history, authState.userInfo]);

  const errorState = useCallback(() => {
    setAuthState({
      userInfo: null,
      isAuthenticated: false,
      permissions: []
    });
    removeAuthStateLS();
  }, [removeAuthStateLS]);

  const redirectToPermCheck = () => history.push('/');

  const noAccessModalCfg = {
    header: 'No Access',
    content: (
      <p className="w-full p-6 font-bold">
        You do not have permission to take that action. Please contact an Administrator
      </p>
    ),
    actions: [{ title: 'Ok', onClick: redirectToPermCheck }]
  };

  const showNoAccessModal = debounce(() => showModal(noAccessModalCfg), 1000, { leading: true });
  axiosContext.authAxios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const ignoreForNoAccess = /\/(login|check-session|csrf-token|permissions)/i;
      const code: number = error && error.response ? error.response.status : 0;
      const url: string | undefined = error?.config?.url;
      const isUnauthorized: boolean = code === 401;
      const isForbidden: boolean = code === 403;

      if (url && !ignoreForNoAccess.test(url) && isForbidden) {
        history.push('/');
        showNoAccessModal();
      } else if (isUnauthorized) {
        // push to login on 401/403 with message
        if (error.response?.data.message === 'ForcedLogOut') {
          errorState();
          showModal(forceLogoutModalCfg);
        }
      }
      return Promise.reject(error);
    }
  );

  const setAuthInfo = useCallback(
    ({ userInfo, permissions = authState.permissions }) => {
      const isAuth = !!(userInfo && userInfo.uid);
      const authStateInfo = {
        userInfo,
        isAuthenticated: isAuth,
        permissions
      };
      setAuthState(authStateInfo);
      setAuthStateLS(authStateInfo);
    },
    [authState.permissions, setAuthStateLS]
  );

  const onPasswordChange = useCallback(() => {
    setAuthInfo({ userInfo: { ...authState.userInfo, shouldUpdatePassword: false } });
    history.push('/checking-permissions');
  }, [authState.userInfo, history, setAuthInfo]);

  const logout = useCallback(
    async (idle = false) => {
      trackEvent({
        category: PiwikCategory.BUTTONS,
        action: PiwikAction.CLICKED,
        name: PiwikName.LOGOUT
      });
      try {
        await axiosContext.authAxios.post('/logout', { idle });
        errorState();
      } catch (err) {
        errorState();
      }
    },
    [axiosContext.authAxios, errorState]
  );

  const login = useCallback(
    async (username: string, password: string) => {
      const { data } = await axiosContext.authAxios.post<UserInfo>('/login', { username, password });
      // sets userInfo and isAuthenticated after login
      setAuthInfo({ userInfo: data, permissions: [] });
      if (data) return true;
      return false;
    },
    [axiosContext.authAxios, setAuthInfo]
  );

  const fetchPermissions = useCallback(async () => {
    const { data: permissions } = await axiosContext.authAxios.get('/permissions');
    setAuthInfo({ userInfo: authState.userInfo, permissions });
    return permissions;
  }, [authState.userInfo, axiosContext.authAxios, setAuthInfo]);

  const manualCheck = useCallback(async () => {
    try {
      const { data } = await axiosContext.authAxios.post('/check-session');
      const { permissions, ...userInfo } = data;

      // prevent re-renders on check-session
      if (isEqual(authStateLS?.permissions, permissions)) {
        // permissions are unchanged from the check-session
        return authState.permissions;
      } else if (authState.permissions.length && !isEqual(authStateLS?.permissions, permissions)) {
        // permissions are changed by an admin
        setTimeout(() => showModal(permissionsChangedModalCfg), 1500);
        setAuthInfo({
          userInfo,
          permissions
        });
        return permissions;
      } else {
        // permissions are empty and changed from initial state from check-session
        setAuthInfo({
          userInfo,
          permissions
        });
        return permissions;
      }
    } catch (err) {
      errorState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosContext.authAxios, authStateLS, authState.permissions, showModal, setAuthInfo]);

  // shouldUpdatePassword has been added as a hack to prevent redirection during password updating.
  // This should be refactored on the backend so that every response from check session has shouldUpdatePassword to better fit the flow
  useInterval(manualCheck, authState.isAuthenticated && !authState.userInfo?.shouldUpdatePassword ? delay : null);

  const value = useMemo(
    () => ({
      authState,
      logout,
      login,
      manualCheck,
      onPasswordChange,
      fetchPermissions,
      authorizationPermissions,
      checkPerm: permManualCheck,
      showNoAccessModal
    }),
    [
      authState,
      logout,
      login,
      manualCheck,
      onPasswordChange,
      fetchPermissions,
      authorizationPermissions,
      permManualCheck,
      showNoAccessModal
    ]
  );

  return <Provider value={value}>{children}</Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('The component this hook must be a descendant of the AuthProvider');
  }
  return context;
}
